import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Spinner,
  Toaster,
  toast,
  useTimeFormatter,
} from "@itoresearch/design-system";
import { InboxIcon, MailIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { useUser } from "../../hooks/use-user";
import {
  useBoxUsername,
  useClaimUsername,
  useBoxMessages,
} from "../../hooks/use-box";
import { useAppAuth } from "../../hooks/use-auth";
import { useDebouncedValue } from "../../hooks/use-debounced-value";
import { noRetryOnAuthErrors } from "../../hooks/use-me";
import { ApiError, api } from "../../lib/api-client";
import {
  BOX_MAIL_DOMAIN,
  SUBADDRESS_SEPARATOR,
  validateUsername,
} from "../../lib/box";

export const Route = createFileRoute("/box/")({
  component: BoxPage,
});

function BoxPage() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner /> Checking session…
      </div>
    );
  if (!user) return null;

  return (
    <div className="space-y-6">
      <Toaster />
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Box</h1>
        <p className="text-muted-foreground">
          Your mailbox on {BOX_MAIL_DOMAIN}.
        </p>
      </div>
      <BoxHome />
    </div>
  );
}

/** Claim card when no username is claimed; the mailbox when one is. */
function BoxHome() {
  const claim = useBoxUsername();

  if (claim.isPending)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner /> Loading mailbox…
      </div>
    );

  if (claim.data) return <Mailbox username={claim.data.username} />;

  // A 404 is the normal "not claimed yet" state.
  if (claim.error instanceof ApiError && claim.error.status === 404) {
    return <ClaimCard />;
  }
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <p className="text-sm">
          Failed to load your mailbox:{" "}
          {claim.error instanceof Error
            ? claim.error.message
            : "unexpected error"}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void claim.refetch()}
        >
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------
// Claim flow
// ------------------------------------------------------------------

function ClaimCard() {
  const [username, setUsername] = useState("");
  const [touched, setTouched] = useState(false);
  const claim = useClaimUsername();

  const validation = validateUsername(username);
  const claimable = validation.ok && username.trim().length > 0;

  // Availability probe: server rules (reserved/taken) as you type.
  const probe = username.trim().toLowerCase();
  const availability = useAvailabilityProbe(probe, validation.ok);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!claimable) return;
    claim.mutate(username.trim(), {
      onSuccess: (result) => {
        toast.add({
          title: "Mailbox claimed",
          type: "success",
          description: `${result.username}@${BOX_MAIL_DOMAIN} is yours.`,
        });
      },
      onError: (error) => {
        toast.add({
          title: "Could not claim username",
          type: "error",
          description:
            error instanceof ApiError ? error.message : "Unexpected error.",
        });
      },
    });
  };

  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <CardTitle>Claim your mailbox</CardTitle>
        <CardDescription>
          Pick a username to receive mail at{" "}
          <span className="font-mono text-foreground">
            username@{BOX_MAIL_DOMAIN}
          </span>
          , and{" "}
          <span className="font-mono text-foreground">
            username{SUBADDRESS_SEPARATOR}anything@{BOX_MAIL_DOMAIN}
          </span>{" "}
          for sub-addresses.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <Field>
            <FieldLabel htmlFor="box-username">Username</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                id="box-username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value.toLowerCase());
                  setTouched(true);
                }}
                placeholder="alice"
                autoComplete="off"
                spellCheck={false}
                maxLength={32}
              />
              <span className="font-mono text-xs text-muted-foreground">
                @{BOX_MAIL_DOMAIN}
              </span>
            </div>
            {touched && !validation.ok && (
              <FieldError errors={[{ message: validation.error }]} />
            )}
            <div aria-live="polite">
              {touched && validation.ok && availability.isPending && (
                <FieldDescription>Checking availability…</FieldDescription>
              )}
              {touched &&
                validation.ok &&
                availability.data &&
                !availability.data.available && (
                  <FieldError
                    errors={[
                      {
                        message:
                          availability.data.reason === "taken"
                            ? "That username is already claimed"
                            : availability.data.reason === "reserved"
                              ? "That username is reserved by the platform"
                              : "That username is not allowed",
                      },
                    ]}
                  />
                )}
              {touched &&
                validation.ok &&
                availability.data?.available && (
                  <FieldDescription>
                    <span className="text-foreground">
                      {username.trim()}@{BOX_MAIL_DOMAIN}
                    </span>{" "}
                    is available.
                  </FieldDescription>
                )}
            </div>
          </Field>
          <Button
            type="submit"
            disabled={!claimable || claim.isPending || availability.isFetching}
          >
            {claim.isPending ? <Spinner /> : null} Claim mailbox
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Availability probe (skips while the client-side rules fail). */
function useAvailabilityProbe(username: string, valid: boolean) {
  const debounced = useDebouncedValue(username, 250);

  const probe = valid ? debounced.trim().toLowerCase() : "";
  return useQuery({
    queryKey: ["box", "availability", probe],
    enabled: probe.length > 0,
    staleTime: 10_000,
    retry: noRetryOnAuthErrors,
    queryFn: () => api.box.availability(probe),
  });
}

// ------------------------------------------------------------------
// Mailbox
// ------------------------------------------------------------------

const PAGE_SIZE = 25;

function Mailbox({ username }: { username: string }) {
  const [offset, setOffset] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const messages = useBoxMessages({ limit: PAGE_SIZE, offset, unreadOnly });
  const formatTime = useTimeFormatter();

  const fullAddress = `${username}@${BOX_MAIL_DOMAIN}`;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MailIcon className="size-4" /> Your addresses
          </CardTitle>
          <CardDescription>
            Mail sent to any of these addresses reaches this mailbox.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 font-mono text-sm">
          <p className="rounded-md border px-3 py-2">{fullAddress}</p>
          <p className="rounded-md border px-3 py-2 text-muted-foreground">
            {username}
            {SUBADDRESS_SEPARATOR}
            <span className="italic">anything</span>@{BOX_MAIL_DOMAIN}
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {messages.data && messages.data.unread > 0 && (
            <Badge>{messages.data.unread} unread</Badge>
          )}
          <Button
            variant={unreadOnly ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setUnreadOnly((v) => !v);
              setOffset(0);
            }}
          >
            {unreadOnly ? "Showing unread" : "Show unread only"}
          </Button>
        </div>
      </div>

      {messages.isPending && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Spinner /> Fetching mail…
        </div>
      )}

      {messages.isError && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm">
              {messages.error instanceof ApiError &&
              messages.error.status === 503
                ? "Mail ingestion is not configured on this deployment."
                : messages.error instanceof Error
                  ? messages.error.message
                  : "Unexpected error."}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void messages.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {messages.data &&
        (messages.data.data.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <InboxIcon />
              </EmptyMedia>
              <EmptyTitle>
                {unreadOnly ? "No unread mail" : "No mail yet"}
              </EmptyTitle>
              <EmptyDescription>
                {unreadOnly ? (
                  "You're all caught up."
                ) : (
                  <>
                    Send an email to{" "}
                    <span className="font-mono">{fullAddress}</span> — it will
                    show up here.
                  </>
                )}
              </EmptyDescription>
            </EmptyHeader>
            {unreadOnly && (
              <EmptyContent>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUnreadOnly(false)}
                >
                  Show all mail
                </Button>
              </EmptyContent>
            )}
          </Empty>
        ) : (
          <>
            <ul className="space-y-2">
              {messages.data.data.map((message) => (
                <li key={message.messageHash}>
                  <Link
                    to="/box/$messageId"
                    params={{ messageId: message.messageHash }}
                    className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    <div
                      className={`flex items-start justify-between gap-4 rounded-lg border px-4 py-3 transition-colors hover:border-foreground/20 ${
                        message.isRead ? "" : "bg-muted/40"
                      }`}
                    >
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex items-center gap-2">
                          {!message.isRead && (
                            <Badge variant="default">new</Badge>
                          )}
                          <p
                            className={`truncate text-sm ${
                              message.isRead
                                ? "text-muted-foreground"
                                : "font-medium text-foreground"
                            }`}
                          >
                            {message.fromName ??
                              message.fromAddress ??
                              "(unknown sender)"}
                          </p>
                        </div>
                        <p
                          className={`truncate text-sm ${
                            message.isRead ? "text-muted-foreground" : ""
                          }`}
                        >
                          {message.subject ?? "(no subject)"}
                        </p>
                      </div>
                      <time className="shrink-0 text-xs text-muted-foreground">
                        {formatTime(message.receivedAt)}
                      </time>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {messages.data.total} message
                {messages.data.total === 1 ? "" : "s"}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Newer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= messages.data.total}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Older
                </Button>
              </div>
            </div>
          </>
        ))}
    </div>
  );
}
